import {Component, EventEmitter, inject, Input, OnInit, Output, ElementRef, ViewChild} from "@angular/core";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute} from "@angular/router";
import {TrainingDtls} from "../../../core/models/trainings/training-dtls";
import {KeyValuePipe, NgClass, NgOptimizedImage} from "@angular/common";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors, ValidatorFn,
  Validators
} from "@angular/forms";
import {TrainingNode} from "../../../core/models/trainings/training-node";
import {trainingsService} from "../../../core/api/services/trainings/trainings.service";
import {CommonService} from "../../../core/api/services/utility-services/common.service";
import {TrainingConstants} from "../../../core/constants/training.constants";
import {EventTypeConstants} from "../../../core/constants/event-type.constants";
import {LogEventDtls} from "../../../core/models/user-profile/log-event-dtls";
import {EventLogReferrerConstants} from "../../../core/constants/event-log-referrer.constants";
import {RoleConstants} from "../../../core/constants/role.constants";
import {MatDatepicker, MatDatepickerInput, MatDatepickerModule,} from '@angular/material/datepicker';
import {EventService} from '../../../core/api/services/events/event.service';
import {State, StaticData} from '../../../core/models/static-data/static-data';
import {MatFormField, MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatNativeDateModule} from '@angular/material/core';
import {MatIconModule} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import AccountTreeDto from '../../../core/models/account/AccountTreeDto';
import {UserService} from '../../../core/api/services/user/user.service';
import {SessionDto} from '../../../core/models/session/sessiondto';
import {LocaleService} from '../../../core/services/locale.service';
import {AddressService} from '../../../core/api/services/address/address.service';
import {environment} from '../../../../environments/environment';
import {SearchableTimezoneDropdownComponent} from '../../common/searchable-timezone-dropdown';

@Component({
  selector: "app-training",
  standalone: true,
  imports: [TranslatePipe, NgOptimizedImage, ReactiveFormsModule, NgClass, MatFormFieldModule, MatDatepickerModule, MatNativeDateModule, MatIconModule, MatDatepicker, MatFormField, MatInputModule, MatFormFieldModule, MatDatepickerInput, MatIconButton, KeyValuePipe, SearchableTimezoneDropdownComponent],
  templateUrl: "./training.component.html",
  styleUrl: "./training.component.css",
})
export class TrainingComponent implements OnInit {
  @Input() referrer?: EventLogReferrerConstants;
  @Output() closeRequested = new EventEmitter<void>();
  @Output() reloadEvents = new EventEmitter<void>();
  @ViewChild("trainingScroll") trainingScroll?: ElementRef<HTMLDivElement>;

  activatedRoute = inject(ActivatedRoute);
  trainingService = inject(trainingsService);
  commonService = inject(CommonService);
  translateService = inject(TranslateService);
  eventService = inject(EventService);
  userService = inject(UserService);
  localeService = inject(LocaleService);
  addressService = inject(AddressService);

  digitalCatalogUrl = environment.digitalCatalogUrl;

  showCancelConfirm = false;
  showEventLocation = false;
  showEstimatedNumberOfAttendees = false;
  showTimeZone = false;
  isPartner: boolean;
  phoneNumberRequired = false;
  loading = false;

  page: number = TrainingConstants.TRAINING_CATALOGS;
  statePlaceholderMode: 'blank' | 'select' | 'no-state' = 'blank';

  trainingForm: FormGroup;
  trainingData: TrainingDtls;
  staticData: StaticData;
  accountTreeDTO: AccountTreeDto;
  sessionDetails: SessionDto;
  filteredStates: State[] = [];
  trainingsNodeList: TrainingNode[];

  errors: string[] = [];
  contactConfEmailError = false;
  sessionCppAvailableFromDates: (string | null)[] = [];
  accountOrgOptions: Array<{ value: number; label: string }> = [];
  statesById: Record<number, State> = {};
  zipLookupTimer: any;

  readonly TrainingConstants = TrainingConstants;

  readonly timeHours: string[] = ['01','02','03','04','05','06','07','08','09','10','11','12'];
  readonly timeMinutes: string[] = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
  readonly timePeriods: string[] = ['AM', 'PM'];

  ENGLISH_LANGUAGE_ID = 82;
  FRENCH_LANGUAGE_ID = 2;
  SPANISH_LANGUAGE_ID = 12;
  DELIVERY_METHOD_ON_SITE = 1835;
  DELIVERY_METHOD_WEBINAR = 1836;
  UNITED_STATES_ID = 840;
  CANADA_ID = 124;
  AUDIENCE_UNITED_STATES = 1842;
  AUDIENCE_CANADA = 1843;
  AUDIENCE_OTHER = 1844;
  USA_LOCALE_ID = 1;

  ngOnInit() {
    this.trainingService.getInitialDtls().subscribe({
      next: (res: TrainingDtls) => {
        this.trainingData = res;
        this.trainingsNodeList = this.trainingData.nodeList;
        this.inItForm(this.referrer);
      }, error: (err) => {
          this.trainingData = this.activatedRoute.snapshot.data["initialDtls"] || ({} as TrainingDtls);
          this.trainingsNodeList = this.trainingData?.nodeList || [];
          this.inItForm(this.referrer);
         },
    });

    this.eventService.getStaticData(this.localeService.getLocaleId()).subscribe((res) => {
      this.staticData = res;
      this.buildStatesById();
    });

    this.commonService.sessiondetails?.subscribe((res) => {
      this.sessionDetails = res;
      this.isPartner = res && res.userRoles?.includes(RoleConstants.PARTNER);
    });

    this.userService.getAccountTrees().subscribe((res) => {
      this.accountTreeDTO = res;
      this.accountOrgOptions = this.flattenAccountTreeToOrgOptions(res)
        .sort((a, b) => a.label.localeCompare(b.label));
    })

    const logEventDtls = new LogEventDtls();
    logEventDtls.eventType = EventTypeConstants.VIEW_TRAINING;

    logEventDtls.msg1 = 'View Training Page 1';
    logEventDtls.msg2 = 'User viewed the Training Catalogs Page';

    this.commonService.logEventDetails(logEventDtls);
  }

  private buildStatesById(): void {
    const states = Object.values(this.staticData?.states ?? {});
    this.statesById = Object.fromEntries(states.map(s => [s.stateId, s])) as Record<number, State>;
  }

  private flattenAccountTreeToOrgOptions(tree: AccountTreeDto | null | undefined): Array<{ value: number; label: string }> {
    const out: Array<{ value: number; label: string }> = [];
    const seen = new Set<number>();

    const walk = (node: AccountTreeDto | null | undefined, depth: number, parentName: string | null) => {
      if (!node?.lightAccountDtos?.length) return;

      for (const light of node.lightAccountDtos) {
        if (light?.orgId != null && !seen.has(light.orgId)) {
          seen.add(light.orgId);
          const label = parentName ? `${parentName} -> ${light.orgName}` : `${light.orgName}`;
          out.push({ value: light.orgId, label });
        }
        walk(light?.childAccountTrees as any, depth + 1, light?.orgName ?? null);
      }
    };

    walk(tree, 0, null);
    return out;
  }

  get sortedLanguages() {
    const priorityIds = [this.ENGLISH_LANGUAGE_ID, this.FRENCH_LANGUAGE_ID, this.SPANISH_LANGUAGE_ID];

    return Object.values(this.staticData?.languages ?? {}).sort((a, b) => {
      const aPriority = priorityIds.indexOf(Number(a.id));
      const bPriority = priorityIds.indexOf(Number(b.id));

      const aIsPriority = aPriority !== -1;
      const bIsPriority = bPriority !== -1;

      if (aIsPriority && bIsPriority) {
        return aPriority - bPriority;
      }

      if (aIsPriority) return -1;
      if (bIsPriority) return 1;

      return a.description.localeCompare(b.description);
    });
  }

  get sortedCountries() {
    const priorityIds = [this.UNITED_STATES_ID, this.CANADA_ID];

    return Object.values(this.staticData?.countries ?? {}).sort((a, b) => {
      const aPriority = priorityIds.indexOf(Number(a.id));
      const bPriority = priorityIds.indexOf(Number(b.id));

      const aIsPriority = aPriority !== -1;
      const bIsPriority = bPriority !== -1;

      if (aIsPriority && bIsPriority) {
        return aPriority - bPriority;
      }

      if (aIsPriority) return -1;
      if (bIsPriority) return 1;

      return a.description.localeCompare(b.description);
    });
  }

  get currentStep(): number {
    switch (this.page) {
      case TrainingConstants.TRAINING_CATALOGS:
        return 1;
      case TrainingConstants.TRAINING_OVERVIEW:
        return 2;
      case TrainingConstants.TRAINING_SESSION_INFORMATION:
        return 3;
      case TrainingConstants.TRAINING_CONTACT_INFORMATION:
        return 4;
      case TrainingConstants.TRAINING_ADDITIONAL_INFORMATION:
          return 5;
      case TrainingConstants.TRAINING_REVIEW:
          return 6;
      default:
          return 1;
        }
    }

    get nextButtonKey(): string {
      if (this.page === TrainingConstants.TRAINING_CATALOGS) return "general.label.button.next";
      if (this.page === TrainingConstants.TRAINING_REVIEW) return "general.label.button.submit";
      return "general.label.button.next";
    }

    get showBack(): boolean {
      return (
        this.page !== TrainingConstants.TRAINING_CATALOGS && this.page !== TrainingConstants.TRAINING_REQUEST_SUCCESS
      );
    }

    cancelTrainingRequest() {
      this.errors = [];

      if (this.page !== TrainingConstants.TRAINING_REQUEST_SUCCESS) {
        const logEventDtls = new LogEventDtls();
        logEventDtls.eventType = EventTypeConstants.VIEW_TRAINING;

        logEventDtls.msg1 = 'Cancel Training Request';
        logEventDtls.msg2 = 'User cancelled the Training request';

        this.commonService.logEventDetails(logEventDtls);
      }

      this.closeRequested.emit();
    }

    selectionChanged(dropdown: string, contactGroup?: FormGroup) {
    const country = this.trainingForm.get("country");
    const zipcode = this.trainingForm.get("zipcode");
    const state = this.trainingForm.get("state");
    const city = this.trainingForm.get("city");
    const address1 = this.trainingForm.get("address1");
    const timeZone = this.trainingForm.get("timeZone");

    if (dropdown === 'deliveryMethod') {
      const deliveryMethodValue = Number(this.trainingForm.get('deliveryMethod')?.value);
      this.showEventLocation = deliveryMethodValue === this.DELIVERY_METHOD_ON_SITE;
      this.showEstimatedNumberOfAttendees = deliveryMethodValue === this.DELIVERY_METHOD_ON_SITE;
      if(deliveryMethodValue == this.DELIVERY_METHOD_ON_SITE) {
        country?.addValidators(Validators.required);
        zipcode?.addValidators(Validators.required);
        state?.addValidators(Validators.required);
        city?.addValidators(Validators.required);
        address1?.addValidators(Validators.required);
        this.contacts.controls.forEach(group => {
          group.get('contactTypeCodeId')?.addValidators(Validators.required);
          group.get('phoneNumber')?.addValidators(Validators.required);
          group.get('phoneNumberAreaCode')?.addValidators([Validators.required, this.phoneNumberAreaCodeValidator()]);
          group.get('contactTypeCodeId')?.updateValueAndValidity();
          group.get('phoneNumber')?.updateValueAndValidity();
          group.get('phoneNumberAreaCode')?.updateValueAndValidity();
        });
        this.phoneNumberRequired = true;
        timeZone?.clearValidators();
        this.showTimeZone = false;
      } else {
        country?.clearValidators();
        zipcode?.clearValidators();
        state?.clearValidators();
        city?.clearValidators();
        address1?.clearValidators();
        this.contacts.controls.forEach(group => {
          group.get('contactTypeCodeId')?.clearValidators();
          group.get('phoneNumber')?.clearValidators();
          group.get('phoneNumberAreaCode')?.setValidators([this.phoneNumberAreaCodeValidator()]);
          group.get('contactTypeCodeId')?.updateValueAndValidity();
          group.get('phoneNumber')?.updateValueAndValidity();
          group.get('phoneNumberAreaCode')?.updateValueAndValidity();
        });
        this.phoneNumberRequired = false;
        timeZone?.addValidators(Validators.required);
        this.showTimeZone = true;

        this.trainingForm.get("country")?.setValue("");
        this.trainingForm.get("zipcode")?.setValue("");
        this.trainingForm.get("state")?.setValue("");
        this.trainingForm.get("city")?.setValue("");
        this.trainingForm.get("address1")?.setValue("");
        this.trainingForm.get("address2")?.setValue("");
      }
    }

    if(dropdown === 'language') {
      if(this.hasDialectsForSelectedLanguage) {
        this.trainingForm.get('dialect')?.addValidators(Validators.required);
      } else {
        this.trainingForm.get('dialect')?.clearValidators();
      }
    }

    if(dropdown === 'country') {
      const countryValue = Number(this.trainingForm.get('country')?.value);

      this.filterStatesByCountry(countryValue)

      const zipCodeCtrl = this.trainingForm.get('zipcode');

      if (countryValue === this.UNITED_STATES_ID || countryValue === this.CANADA_ID) {
        zipCodeCtrl?.setValidators([Validators.required]);
      } else {
        zipCodeCtrl?.clearValidators();
      }

      zipCodeCtrl?.updateValueAndValidity();
    }
  }

  filterStatesByCountry(countryValue: number): void {
    const allStates = Object.values(this.staticData?.states ?? {});

    this.filteredStates = countryValue
      ? allStates
        .filter(state => state.countryCode === countryValue)
        .sort((a, b) => a.stateName.localeCompare(b.stateName))
      : [];

    if (!countryValue) {
      this.statePlaceholderMode = 'blank';
    } else if (this.filteredStates.length === 0) {
      this.statePlaceholderMode = 'no-state';
    } else {
      this.statePlaceholderMode = 'select';
    }

    const stateCtrl = this.trainingForm.get('state');
    stateCtrl?.setValue(null);
    stateCtrl?.clearValidators();

    if (countryValue && this.filteredStates.length > 0) {
      stateCtrl?.addValidators(Validators.required);
    }

    stateCtrl?.updateValueAndValidity();
  }

  private wireZipLookup(): void {
    const zipCtrl = this.trainingForm?.get('zipcode');
    const countryCtrl = this.trainingForm?.get('country');

    zipCtrl?.valueChanges.subscribe(() => {
      clearTimeout(this.zipLookupTimer);

      this.zipLookupTimer = setTimeout(() => {
        this.lookupZipCode();
      }, 300);
    });

    countryCtrl?.valueChanges.subscribe(() => {
      this.lookupZipCode();
    });
  }

  inItForm = (referrer?: EventLogReferrerConstants) => {
    if (this.page === (TrainingConstants as any).TRAINING_OVERVIEW || this.page === TrainingConstants.TRAINING_REQUEST_SUCCESS) {
      this.trainingForm = new FormGroup({
        deliveryMethod: new FormControl("", [Validators.required]),
        audience: new FormControl(this.getAudience(), [Validators.required]),
        language: new FormControl("", [Validators.required]),
        dialect: new FormControl("", [Validators.required]),
        country: new FormControl("", [Validators.required]),
        zipcode: new FormControl(""),
        state: new FormControl(null, [Validators.required]),
        city: new FormControl("", [Validators.required]),
        address1: new FormControl("", [Validators.required]),
        address2: new FormControl(""),
        account: new FormControl(this.sessionDetails?.orgId ? this.sessionDetails.orgId : ""),
        additionalInformation: new FormControl(""),
        eventDate: new FormControl("", [Validators.required, this.minDateAfterDays(30)]),
        sessions: new FormArray<FormGroup>([]),
        contacts: new FormArray<FormGroup>([]),
      });

    }

    this.wireZipLookup();

  };

  get sessions(): FormArray<FormGroup> {
    return this.trainingForm.get('sessions') as FormArray<FormGroup>;
  }

  get contacts(): FormArray<FormGroup> {
    return this.trainingForm.get('contacts') as FormArray<FormGroup>;
  }

  private availableFromValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = control.parent as FormGroup;
      if (!parent) return null;

      const titleId = Number(parent.get('title')?.value);
      if (!titleId) return null;

      const title = Object.values(this.staticData?.accountEventTitles ?? {})
        .find(t => t.titleId === titleId);
      if (!title?.availableFromDate) return null;

      const eventDate = control.value;
      if (!eventDate) return null;

      const available = new Date(title.availableFromDate);
      const availableUTC = Date.UTC(available.getUTCFullYear(), available.getUTCMonth(), available.getUTCDate());
      const sel = eventDate instanceof Date ? new Date(eventDate) : new Date(String(eventDate));
      const selectedUTC = Date.UTC(sel.getFullYear(), sel.getMonth(), sel.getDate());

      return selectedUTC < availableUTC ? { availableFrom: { date: title.availableFromDate } } : null;
    };
  }

  private createSessionGroup(): FormGroup {
    const group = new FormGroup({
      title: new FormControl('', Validators.required),
      eventDate: new FormControl('', [Validators.required, this.availableFromValidator()]),
      timeZone: new FormControl('', Validators.required),
      startTimeHour: new FormControl('', Validators.required),
      startTimeMinute: new FormControl('', Validators.required),
      startTimePeriod: new FormControl('', Validators.required),
      numberOfAttendees: new FormControl(''),
    });

    group.get('title')?.valueChanges.subscribe(() => {
      group.get('eventDate')?.updateValueAndValidity();
    });

    return group;
  }

  private createContactGroup(isAdditionalContact: boolean): FormGroup {
    const phoneParts = this.extractPhoneParts(this.trainingData.fullPhoneNumber);
    const isOnSite = Number(this.trainingForm.get('deliveryMethod')?.value) === this.DELIVERY_METHOD_ON_SITE;

    return new FormGroup({
      contactTypeCodeId: new FormControl('', isOnSite ? Validators.required : null),
      firstName: new FormControl(isAdditionalContact ? '' : this.trainingData.firstName, Validators.required),
      lastName: new FormControl(isAdditionalContact ? '' : this.trainingData.lastName, Validators.required),
      contactTitle: new FormControl(isAdditionalContact ? '' : this.trainingData.contactTitle),
      email: new FormControl(isAdditionalContact ? '' : this.trainingData.contactEmail, [Validators.required, this.emailValidator()]),
      phoneNumber: new FormControl(isAdditionalContact ? '' : phoneParts.phoneNumber, {
        validators: isOnSite
          ? [Validators.required, this.phoneNumberMinDigitsValidator(7)]
          : [this.phoneNumberMinDigitsValidator(7)],
        updateOn: 'blur'
      }),
      phoneNumberExt: new FormControl(isAdditionalContact ? '' : phoneParts.ext),
      phoneNumberCountryCode: new FormControl(isAdditionalContact ? '' : phoneParts.countryCode),
      phoneNumberAreaCode: new FormControl(isAdditionalContact ? '' : phoneParts.areaCode,
        isOnSite
          ? [Validators.required, this.phoneNumberAreaCodeValidator()]
          : [this.phoneNumberAreaCodeValidator()]),
      includeInAccConfEmailInd: new FormControl(true, Validators.required),
    });
  }

  extractPhoneParts(phoneNumber: string | null | undefined) {

    if (!phoneNumber?.trim()) {
      return {
        countryCode: "",
        areaCode: "",
        phoneNumber: "",
        ext: "",
      };
    }

    const match = phoneNumber.match(
      /^\s*(?:\+?(\d+)\s*)?\((\d{3})\)\s*(\d{3})-?(\d{4})(?:\s*(?:ext\.?|x)\s*(\d+))?\s*$/i
    );

    if (!match) {
      throw new Error("Invalid phone number format");
    }

    const [, countryCode, areaCode, numberPart1, numberPart2, ext] = match;

    return {
      countryCode: countryCode ?? "",
      areaCode: `(${areaCode})`,
      phoneNumber: `${numberPart1}-${numberPart2}`,
      ext: ext ?? "",
    };
  }

  private phoneNumberAreaCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = String(control.value ?? '').trim();

      if (!rawValue) {
        return null;
      }

      const digits = rawValue.replace(/\D/g, '');

      return digits.length === 3
        ? null
        : {
          phoneNumberAreaCodeExactDigits: {
            requiredDigits: 3,
            actualDigits: digits.length,
          }
        };
    };
  }

  get additionalInformationLength(): number {
    return (this.trainingForm.get('additionalInformation')?.value || '').length;
  }

  emailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const emailRegex =
        /^(?!\.)(?!.*\.{2,})[a-zA-Z0-9._%+-]+(?<!\.)@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      return emailRegex.test(value) ? null : { email: true };
    };
  }

  private minDateAfterDays(days: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) return null;

      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);

      const minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      minDate.setDate(minDate.getDate() + days);

      return selectedDate >= minDate ? null : { minDateAfterDays: true };
    };
  }

  get minEventDate(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 31);
    return d;
  }

  private getAudience() {
    for (const [, country] of Object.entries(this.staticData.audience)) {
      if(country.description === this.trainingData.contactCountry) {
        return country.id;
      }
    }
    return "";
  }

  private scrollToTop(): void {
    this.trainingScroll?.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  submitTrainingRequest(page: number) {
    this.errors = [];
    this.contactConfEmailError = false;

    if (page === TrainingConstants.TRAINING_CATALOGS) {
      this.page = (TrainingConstants as any).TRAINING_OVERVIEW;
      this.inItForm();
      this.logTrainingEvent();
      return;
    }

    Object.keys(this.trainingForm.controls).forEach((key) => {
      this.trainingForm.controls[key].markAsDirty();
    });

    if (page === (TrainingConstants as any).TRAINING_OVERVIEW) {
      this.trainingForm.markAllAsTouched();
      this.trainingForm.updateValueAndValidity();

      const pageTwoControls = [
        "deliveryMethod",
        "audience",
        "language",
      ]

      if(this.hasDialectsForSelectedLanguage) {
        pageTwoControls.push("dialect");
      }

      if(Number(this.trainingForm.get('deliveryMethod')?.value) === this.DELIVERY_METHOD_ON_SITE) {
        pageTwoControls.push("country", "city", "address1");
      }

      if(Number(this.trainingForm.get('country')?.value == this.UNITED_STATES_ID) || Number(this.trainingForm.get('country')?.value == this.CANADA_ID)) {
        pageTwoControls.push("zipcode", "state");
      }

      const ok = this.validateControls(pageTwoControls)
      if (ok) {
        this.setFirstPageValues();
        this.page = TrainingConstants.TRAINING_SESSION_INFORMATION;
        this.inItForm();
        if(this.sessions.length === 0) this.addSession();
        this.scrollToTop();
        this.logTrainingEvent();
      } else {
        this.pushStepErrors(pageTwoControls);
        this.scrollToTop();
      }
      return;
    }

    Object.keys(this.trainingForm.controls).forEach((key) => {
      this.trainingForm.controls[key].markAsDirty();
    });

    if (page === TrainingConstants.TRAINING_SESSION_INFORMATION) {
      this.trainingForm.markAllAsTouched();
      this.trainingForm.updateValueAndValidity();
      this.sessions.controls.forEach((group, i) => {
        const deliveryMethodValue = Number(this.trainingForm.get('deliveryMethod')?.value);
        const timeZoneCtrl = group.get('timeZone');
        const noOfAttendeesCtrl = group.get('numberOfAttendees');

        if(deliveryMethodValue === this.DELIVERY_METHOD_ON_SITE) {
          timeZoneCtrl?.clearValidators();
          timeZoneCtrl?.updateValueAndValidity();
          noOfAttendeesCtrl?.addValidators([Validators.required, this.nonZeroValidator()]);
          noOfAttendeesCtrl?.updateValueAndValidity();
        } else {
          timeZoneCtrl?.addValidators(Validators.required);
          timeZoneCtrl?.updateValueAndValidity();
          noOfAttendeesCtrl?.clearValidators();
          noOfAttendeesCtrl?.updateValueAndValidity();
        }
      });

      const ok = this.sessions.valid;
      if (ok) {
        this.trainingData.selectedAccount = this.accountOrgOptions.find(o => o.value === Number(this.trainingForm.get('account')?.value))?.label ?? '';
        this.addSessions();
        this.page = TrainingConstants.TRAINING_CONTACT_INFORMATION;
        if(this.contacts.length === 0) this.addContact();
        this.scrollToTop();
        this.logTrainingEvent();
      } else {
        this.pushSessionErrors();
        this.scrollToTop();
      }
      return;
    }

    if (page === TrainingConstants.TRAINING_CONTACT_INFORMATION) {

      this.trainingForm.markAllAsTouched();
      this.contacts.controls.forEach(group => {
        Object.values((group as FormGroup).controls).forEach(ctrl => {
          ctrl.updateValueAndValidity({ emitEvent: false });
        });
      });
      this.trainingForm.updateValueAndValidity();

      const formValid = this.contacts.valid;
      const confEmailValid = this.contacts.length <= 1 ||
        this.contacts.getRawValue().some((c: any) => c.includeInAccConfEmailInd === true);

      if (formValid && confEmailValid) {
        this.addContacts();
        this.page = TrainingConstants.TRAINING_ADDITIONAL_INFORMATION;
        this.scrollToTop();
        this.logTrainingEvent();
      } else {
        if (!formValid) this.pushContactsError();
        if (!confEmailValid) this.contactConfEmailError = true;
        this.scrollToTop();
      }
      return;
    }

    if (page === TrainingConstants.TRAINING_ADDITIONAL_INFORMATION) {
      this.trainingData.additionalInformation = this.trainingForm.get("additionalInformation")?.value;
      this.page = TrainingConstants.TRAINING_REVIEW;
      this.scrollToTop();
      this.logTrainingEvent();
      return;
    }

    if (page === TrainingConstants.TRAINING_REVIEW) {
      this.trainingData.selectedLocaleId = this.localeService.getLocaleId();
      this.loading = true;
      this.trainingService.requestTraining(this.trainingData).subscribe({
        next: (res) => {
          if (res === 'OK') {
            this.page = TrainingConstants.TRAINING_REQUEST_SUCCESS;
            this.logTrainingEvent();
            this.reloadEvents.emit();
          }
          this.loading = false;
        },
        error: (err) => {
          if (err.status === 422) {
            try {
              const body = typeof err.error === 'string' ? JSON.parse(err.error) : err.error;
              const violations: { titleId: number; availableFromDate: string }[] = body?.violations ?? [];
              // Build a map of titleId -> formatted available-from date
              const titleDateMap = new Map<number, string>();
              violations.forEach(v => {
                if (v.titleId && v.availableFromDate) {
                  titleDateMap.set(v.titleId, this.formatAvailableFromDate(v.availableFromDate));
                }
              });
              if (titleDateMap.size > 0) {
                this.sessionCppAvailableFromDates = this.sessions.controls.map((sessionGroup) => {
                  const titleId = Number((sessionGroup as FormGroup).get('title')?.value);
                  return titleDateMap.get(titleId) ?? null;
                });

                // Update local staticData with CPP-corrected availableFromDate so the validator uses the real date
                violations.forEach(v => {
                  const title = Object.values(this.staticData?.accountEventTitles ?? {})
                    .find(t => t.titleId === v.titleId);
                  if (title) {
                    title.availableFromDate = v.availableFromDate;
                  }
                });

                // Re-trigger validation so the corrected date is enforced immediately
                this.sessions.controls.forEach(sessionGroup => {
                  (sessionGroup as FormGroup).get('eventDate')?.updateValueAndValidity();
                });
              }
            } catch (_) {}
            this.page = TrainingConstants.TRAINING_SESSION_INFORMATION;
            this.scrollToTop();
          }
          this.loading = false;
        }
      });
    }
  }

  nonZeroValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = Number(control.value);

      if (control.value === null || control.value === undefined || control.value === '') {
        return null;
      }

      return value !== 0 ? null : { nonZero: true };
    };
  }

  addSessions() {
    const rows = this.sessions.getRawValue() as Array<{
      title: unknown;
      eventDate: unknown;
      timeZone: unknown;
      startTimeHour: unknown;
      startTimeMinute: unknown;
      startTimePeriod: unknown;
      numberOfAttendees: unknown;
    }>;

    const branchOrgId = Number(this.trainingForm.get('account')?.value);

    this.trainingData.sessions = rows.map((r) => {
      const eventDate =
        r.eventDate instanceof Date ? r.eventDate : new Date(String(r.eventDate));

      return {
        branchOrgId,
        trnEventTitleId: Number(r.title ?? ''),
        eventDate: this.formatEventDate(eventDate),
        trnTimezoneId: Number(r.timeZone ?? ''),
        startDatetime: this.formatStartDateTime(r.eventDate, `${r.startTimeHour}:${r.startTimeMinute} ${r.startTimePeriod}`),
        attendeesEstimate: Number(r.numberOfAttendees ?? 0),
      };
    });
  }

  private formatEventDate(date: Date): string {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();

    return `${mm}/${dd}/${yyyy}`;
  }

  private formatStartDateTime(eventDate: unknown, timeValue: unknown): string {
    const date =
      eventDate instanceof Date ? eventDate : new Date(String(eventDate));

    if (isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const timeStr = String(timeValue ?? '').trim();
    const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!timeMatch) {
      return '';
    }

    let hours = parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const period = timeMatch[3].toUpperCase();

    if (period === 'AM') {
      if (hours === 12) hours = 0;
    } else {
      if (hours !== 12) hours += 12;
    }

    const hh = String(hours).padStart(2, '0');
    const min = String(minutes).padStart(2, '0');

    return `${year}-${month}-${day}T${hh}:${min}:00.000+00:00`;
  }

  addContacts() {
    const rows = this.contacts.getRawValue() as Array<{
      contactTypeCodeId: unknown;
      firstName: unknown;
      lastName: unknown;
      contactTitle: unknown;
      email: unknown;
      phoneNumber: unknown;
      phoneNumberExt: unknown;
      phoneNumberCountryCode: unknown;
      phoneNumberAreaCode: unknown;
      includeInAccConfEmailInd: unknown;
    }>;

    this.trainingData.contacts = rows.map((r) => ({
      contactTypeCodeId: Number(r.contactTypeCodeId),
      firstName: String(r.firstName ?? ''),
      lastName: String(r.lastName ?? ''),
      contactTitle: String(r.contactTitle ?? ''),
      emailAddress: String(r.email ?? ''),
      phones: [
        {
          phoneNumber: String(r.phoneNumber ?? ''),
          extension: String(r.phoneNumberExt ?? ''),
          countryCode: String(r.phoneNumberCountryCode ?? ''),
          areaCode: String(r.phoneNumberAreaCode ?? '').replace(/\D/g, ''),
        },
      ],
      includeInAccConfEmailInd: Boolean(r.includeInAccConfEmailInd ?? false),
    }));
  }

  validateControls(controlNames: string[]) {
    this.errors = [];
    for(const name of controlNames) {
      const ctrl = this.trainingForm.get(name);
      if(!ctrl) continue;

      ctrl.markAsTouched();
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity({onlySelf: true});
    }
    this.trainingForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    return controlNames.every((name) => this.trainingForm.get(name)?.valid !== false);
  }

  private pushContactsError(): void {
    if (this.contacts.length === 0) {
      this.errors.push('training.sessions');
      return;
    }

    this.contacts.controls.forEach((group, i) => {
      const fields = ['contactTypeCodeId', 'firstName', 'lastName', 'email', 'includeInAccConfEmailInd', ...(this.phoneNumberRequired
        ? ['phoneNumber', 'phoneNumberAreaCode']
        : []), ] as const;

      for (const field of fields) {
        const ctrl = group.get(field);
        if (ctrl?.errors) {
          if(i == 0) {
            this.errors.push(
              `${this.translateService.instant('training.primaryContact')} ${this.translateService.instant(`training.${field}`)}`
            );          } else {
            this.errors.push(
              `${this.translateService.instant('training.additionalContact')} ${i + 1} ${this.translateService.instant(`training.${field}`)}`
            );
          }
        }
      }
    });
  }

  private pushSessionErrors(): void {
    if (this.sessions.length === 0) {
      this.errors.push('training.contacts');
      return;
    }

    this.sessions.controls.forEach((group, i) => {
      const fields = ['title', 'eventDate'] as const;

      for (const field of fields) {
        const ctrl = group.get(field);
        if (ctrl?.errors) {
          if (field === 'eventDate' && ctrl.errors['availableFrom'] && Object.keys(ctrl.errors).length === 1) {
            this.errors.push(
              `${this.translateService.instant('training.session')} ${i + 1} ${this.translateService.instant('training.eventDateBeforeAvailableFrom', { date: this.formatAvailableFromDate(ctrl.errors['availableFrom'].date) })}`
            );
            continue;
          }
          this.errors.push(
            `${this.translateService.instant('training.session')} ${i + 1} ${this.translateService.instant(`training.${field}`)}`
          );
        }
      }

      if (this.sessionCppAvailableFromDates[i]) {
        this.errors.push(
          `${this.translateService.instant('training.session')} ${i + 1} ${this.translateService.instant('training.eventDate')}`
        );
      }

      const startTimeHourCtrl = group.get('startTimeHour');
      const startTimeMinuteCtrl = group.get('startTimeMinute');
      const startTimePeriodCtrl = group.get('startTimePeriod');
      if (startTimeHourCtrl?.errors || startTimeMinuteCtrl?.errors || startTimePeriodCtrl?.errors) {
        this.errors.push(
          `${this.translateService.instant('training.session')} ${i + 1} ${this.translateService.instant('training.startTime')}`
        );
      }

      const deliveryMethodValue = Number(this.trainingForm.get('deliveryMethod')?.value);;
      const timeZoneCtrl = group.get('timeZone');
      const noOfAttendeesCtrl = group.get('numberOfAttendees');

      if (deliveryMethodValue !== this.DELIVERY_METHOD_ON_SITE) {

        if (timeZoneCtrl?.errors) {
          this.errors.push(
            `${this.translateService.instant('training.session')} ${i + 1} ${this.translateService.instant('training.timeZone')}`
          );
        }
      } else {
        timeZoneCtrl?.clearValidators();
        timeZoneCtrl?.updateValueAndValidity();
        noOfAttendeesCtrl?.addValidators(Validators.required);
        if (noOfAttendeesCtrl?.errors) {
          this.errors.push(
            `${this.translateService.instant('training.session')} ${i + 1} ${this.translateService.instant('training.estimatedNumberOfAttendees')}`
          );        }
      }
    });
  }

  private pushStepErrors(controlNames: string[]) {
    for (const name of controlNames) {
      const ctrl = this.trainingForm.get(name);
      if (ctrl?.errors) this.errors.push("training." + name);
    }
  }

    navigateToPreviousPage(page: number) {
        this.errors = [];

        if (page === (TrainingConstants as any).TRAINING_OVERVIEW) {
            this.page = TrainingConstants.TRAINING_CATALOGS;
            this.logTrainingEvent();
          this.scrollToTop();
            return;
        }

        if (page === TrainingConstants.TRAINING_SESSION_INFORMATION) {
            this.page = (TrainingConstants as any).TRAINING_OVERVIEW;
            this.logTrainingEvent();
          this.scrollToTop();
            return;
        }

        if (page === TrainingConstants.TRAINING_CONTACT_INFORMATION) {
          this.page = TrainingConstants.TRAINING_SESSION_INFORMATION;
          this.logTrainingEvent();
          this.scrollToTop();

        }

        if(page === TrainingConstants.TRAINING_ADDITIONAL_INFORMATION) {
          this.page = TrainingConstants.TRAINING_CONTACT_INFORMATION;
          this.logTrainingEvent();
          this.scrollToTop();
        }

        if (page === TrainingConstants.TRAINING_REVIEW) {
            this.page = TrainingConstants.TRAINING_ADDITIONAL_INFORMATION;
            this.logTrainingEvent();
            this.scrollToTop();
            return;
        }

        if (page === TrainingConstants.TRAINING_REQUEST_SUCCESS) {
            this.page = TrainingConstants.TRAINING_CATALOGS;
            this.logTrainingEvent();
            this.ngOnInit();
        }
    }

  setFirstPageValues() {

    if (!this.trainingData.address) {
      this.trainingData.address = {
        addressId: 0,
        address1: '',
        address2: '',
        city: '',
        stateProvinceId: 0,
        zip: '',
        countryCode: ''
      };
    }

      this.trainingData.deliveryMethod = this.trainingForm.get("deliveryMethod")?.value;
      this.trainingData.audience = this.trainingForm.get("audience")?.value;
      this.trainingData.language = this.trainingForm.get("language")?.value;
      this.trainingData.dialect = this.trainingForm.get("dialect")?.value;
      this.trainingData.country = this.trainingForm.get("country")?.value;
      this.trainingData.address.zip = this.trainingForm.get("zipcode")?.value;
      this.trainingData.address.stateProvinceId = this.trainingForm.get("state")?.value;
      this.trainingData.address.city = this.trainingForm.get("city")?.value;
      this.trainingData.address.countryCode = this.trainingForm.get("country")?.value;
      this.trainingData.address.address1 = this.trainingForm.get("address1")?.value;
      this.trainingData.address.address2 = this.trainingForm.get("address2")?.value;
  }

  private logTrainingEvent(): void {
    const isSubmitSuccess = this.page === TrainingConstants.TRAINING_REQUEST_SUCCESS;

    const pageNames: Record<number, string> = {
      [TrainingConstants.TRAINING_CATALOGS]: 'Training Catalogs Page',
      [TrainingConstants.TRAINING_OVERVIEW]: 'Training Overview Page',
      [TrainingConstants.TRAINING_SESSION_INFORMATION]: 'Training Session Information Page',
      [TrainingConstants.TRAINING_CONTACT_INFORMATION]: 'Training Contact Information Page',
      [TrainingConstants.TRAINING_ADDITIONAL_INFORMATION]: 'Training Additional Information Page',
      [TrainingConstants.TRAINING_REVIEW]: 'Training Review Page',
    };

    const logEventDtls = new LogEventDtls();
    logEventDtls.eventType = isSubmitSuccess
      ? EventTypeConstants.SUBMIT_TRAINING
      : EventTypeConstants.VIEW_TRAINING;

    if (isSubmitSuccess) {
      logEventDtls.msg1 = 'Submit Training Request';
      logEventDtls.msg2 = 'User submitted a training request';
    } else {
      const pageName = pageNames[this.page] ?? 'Training Page';
      logEventDtls.msg1 = `View Training Page ${this.page}`;
      logEventDtls.msg2 = `User viewed the ${pageName}`;
    }

    this.commonService.logEventDetails(logEventDtls);
  }

    openCancelConfirm() {
        this.showCancelConfirm = true;
    }

    closeCancelConfirm() {
        this.showCancelConfirm = false;
    }

    confirmCancelRequest() {
        this.showCancelConfirm = false;
        this.cancelTrainingRequest();
    }

    resetTrainingRequest() {
        this.page = TrainingConstants.TRAINING_CATALOGS;
        this.ngOnInit();
    }

    addSession() {
      this.sessions.push(this.createSessionGroup());
      this.sessionCppAvailableFromDates.push(null);
    }

  removeSession(index: number): void {
    this.sessions.removeAt(index);
    this.sessionCppAvailableFromDates.splice(index, 1);
  }

  addContact() {
    this.contacts.push(this.createContactGroup(this.contacts.length >= 1));
    const isOnSite = Number(this.trainingForm.get('deliveryMethod')?.value) === this.DELIVERY_METHOD_ON_SITE;
    if (isOnSite) {
      this.phoneNumberRequired = true;
    }
    this.updateConfEmailControls();
  }

  removeContact(index: number) {
    this.contacts.removeAt(index);
    this.updateConfEmailControls();
  }

  private updateConfEmailControls(): void {
    if (this.contacts.length === 1) {
      const ctrl = this.contacts.at(0).get('includeInAccConfEmailInd');
      ctrl?.setValue(true);
      ctrl?.disable();
    } else {
      this.contacts.controls.forEach(group => {
        group.get('includeInAccConfEmailInd')?.enable();
      });
    }
  }

  showError(control: string, group?: FormGroup): boolean {
    const formControl = group ? group.get(control) : this.trainingForm.get(control);
    const countryValue = Number(this.trainingForm.get('country')?.value);

    if (control === 'zipcode' || control === 'state') {
      if (countryValue !== this.UNITED_STATES_ID && countryValue !== this.CANADA_ID) {
        return false;
      }
    }

    return !!formControl && (
      formControl.invalid &&
      (formControl.dirty || formControl.touched || this.errors.some(error => error.includes(control)))
    );
  }

  get selectedLanguageId(): number {
    return Number(this.trainingForm.get('language')?.value);
  }

  get filteredDialects() {
    return Object.values(this.staticData?.dialects ?? {}).filter(
      dialect => dialect.languageId === this.selectedLanguageId
    );
  }

  get hasDialectsForSelectedLanguage(): boolean {
    return this.filteredDialects.length > 0;
  }

  get filteredTitles() {
    const deliveryMethod = Number(this.trainingForm.controls["deliveryMethod"]?.value);
    const audience = Number(this.trainingForm.controls["audience"]?.value);

    const filtered = Object.values(this.staticData?.accountEventTitles ?? {}).filter((title) => {
      const matchesDeliveryMethod =
        (deliveryMethod === this.DELIVERY_METHOD_ON_SITE && title.methodOnsiteInd) ||
        (deliveryMethod === this.DELIVERY_METHOD_WEBINAR && title.methodWebinarInd);

      const matchesAudience =
        (audience === this.AUDIENCE_UNITED_STATES && title.audienceUsaInd) ||
        (audience === this.AUDIENCE_CANADA && title.audienceCanadaInd) ||
        (audience === this.AUDIENCE_OTHER && title.audienceOtherInd);

      return matchesDeliveryMethod && matchesAudience;
    });

    const locale = this.localeService.getLocaleStr();
    return filtered.sort((a, b) =>
      a.titleName.localeCompare(b.titleName, locale, { sensitivity: 'base' })
    );
  }

  getSelectedTitleAvailableFrom(sessionGroup: FormGroup): string | null {
    const titleId = Number(sessionGroup.get('title')?.value);
    if (!titleId) return null;
    const title = Object.values(this.staticData?.accountEventTitles ?? {})
      .find(t => t.titleId === titleId);
    return title?.availableFromDate ?? null;
  }

  isFutureAvailableFrom(dateStr: string | null | undefined): boolean {
    if (!dateStr) return false;
    const available = new Date(dateStr);
    const availableUTC = Date.UTC(available.getUTCFullYear(), available.getUTCMonth(), available.getUTCDate());
    const today = new Date();
    const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    return availableUTC > todayUTC;
  }

  formatAvailableFromDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const locale = this.localeService.getLocaleStr();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    const yyyy = date.getUTCFullYear();
    return locale === 'en-US' ? `${mm}/${dd}/${yyyy}` : `${dd}/${mm}/${yyyy}`;
  }

  onTitleChanged(sessionIndex: number): void {
    this.sessionCppAvailableFromDates[sessionIndex] = null;
    (this.sessions.at(sessionIndex) as FormGroup).get('eventDate')?.updateValueAndValidity();
  }

  onEventDateChanged(sessionIndex: number): void {
    this.sessionCppAvailableFromDates[sessionIndex] = null;
  }

  get sortedTimeZones() {
    return Object.values(this.staticData?.timeZones ?? {}).sort((a, b) =>
      a.description.localeCompare(b.description)
    );
  }

  private lookupZipCode(): void {
    const zipCtrl = this.trainingForm.get('zipcode');
    const countryCode = Number(this.trainingForm.get('country')?.value);
    const rawZip = String(zipCtrl?.value ?? '').trim();

    if (![this.UNITED_STATES_ID, this.CANADA_ID].includes(countryCode)) {
      return;
    }

    const country = countryCode === this.CANADA_ID ? 'CA' : 'US';

    let zipCode = rawZip;

    if (country === 'CA') {
      zipCode = rawZip.replace(/\s+/g, '').toUpperCase();
      if (zipCode.length === 6) {
        zipCode = `${zipCode.slice(0, 3)} ${zipCode.slice(3)}`;
      }
    }

    this.addressService.getZipCityState(zipCode, country).subscribe((res) => {
      if (res.status === 'SUCCESS' && res.zipCodes?.length > 0) {
        const stateCode = res.zipCodes[0]?.stateCode;
        const city = res.zipCodes[0]?.city;

        if (stateCode && this.staticData?.states) {
          const matchedState = Object.values(this.staticData.states).find(
            state => state.stateAbbreviation === stateCode
          );

          if (matchedState) {
            this.trainingForm.get('state')?.setValue(matchedState.stateId);
          }
        }

        if (city) {
          this.trainingForm.get('city')?.setValue(city);
        }
      } else {
        console.log('Error getting zip code');
      }
    });
  }

  getConvertedTime(time: string | null | undefined): string | null {
    if (!time) {
      return null;
    }

    const match = time.match(/T(\d{2}):(\d{2})/);
    if (!match) {
      return null;
    }

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    const displayMinutes = String(minutes).padStart(2, '0');

    return `${displayHour}:${displayMinutes} ${period}`;
  }

  private phoneNumberMinDigitsValidator(minDigits = 7): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const rawValue = String(control.value ?? '').trim();

      if (!rawValue) {
        return null;
      }

      const digitCount = rawValue.replace(/\D/g, '').length;

      return digitCount >= minDigits ? null : {
        phoneNumberMinDigits: {
          requiredDigits: minDigits,
          actualDigits: digitCount,
        }
      };
    };
  }

  protected readonly Number = Number;
}
